import { UploadFileTypeEnum } from 'api-hooks/upload/upload.model';
import { PlusCircleSVG, TrashSVG } from 'common/assets';
import { GenderTypeEnum, EnumList } from 'common/constants/enum';
import stylesColors from 'common/styles/styles.colors';
import { themedStyled } from 'common/styles/styletron.theme';
import ParsePassport, {
  PassportType,
} from 'components/common/parse-excel/parse-passport';
import Separator from 'components/common/separator';
import { Input } from 'components/elements';
import { Button } from 'components/elements/buttons';
import { useFormState } from 'components/elements/form';
import Select from 'components/elements/master/select-input';
import Text from 'components/elements/text';
import EnumSelectInput from 'legacy-modules/master/select-input/enum';
import {
  CardSection,
  FullContainer,
  OneThirdContainer,
  SectionContainer as RawSectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { GuestFormType } from '../form-type';
import { usePassportCsv } from '../hook/use-passport-csv';

export default function PassportCard() {
  const { t } = useTranslation();
  const { control, setValue, clearErrors } = useFormContext<GuestFormType>();
  const value = useWatch({ control, name: 'hasPassport' });
  const {
    passportCsvData,
    passportValue,
    setPassportCsvData,
    setPassportValue,
  } = usePassportCsv();

  const passportOptions = React.useMemo(
    () =>
      passportCsvData.map((datum) => ({
        id: datum.id,
        label: `${datum.givenName}${
          datum.familyName ? ` ${datum.familyName}` : ''
        } - ${datum.documentNo}`,
        extra: datum,
      })),
    [passportCsvData],
  );

  const onAddPassport = () => {
    setValue('hasPassport', true);
    setValue('passport', {
      number: '',
      files: [],
      type: '',
      expiredAt: null,
      dateOfBirth: null,
      givenName: '',
      identifierNumber: '',
      issueState: '',
      placeOfBirth: '',
      familyName: '',
      nationality: '',
      gender: '',
      id: undefined,
    });
    clearErrors('passport');
  };

  const onRemovePassport = () => {
    setValue('hasPassport', false);
    setValue('passport', null);
    clearErrors('passport');
  };

  const onChangePassport = ({
    result,
    isReupload,
  }: {
    result: PassportType[];
    isReupload?: boolean;
  }) => {
    setPassportCsvData(result);
    setPassportValue(null);
    if (!isReupload) {
      onAddPassport();
    }
  };

  const onSetPassport = (passport: PassportType) => {
    setValue(
      'passport',
      {
        number: passport.documentNo || '',
        files: [],
        type: '',
        expiredAt: passport.expireDate || null,
        dateOfBirth: passport.birthday || null,
        givenName: passport.givenName || '',
        identifierNumber: passport.personalNo || '',
        issueState: passport.issueState || '',
        placeOfBirth: passport.birthPlace || '',
        familyName: passport.familyName || '',
        nationality: passport.nationality || '',
        gender:
          passport.sex === 'F' ? GenderTypeEnum.Female : GenderTypeEnum.Male,
        id: undefined,
      },
      {
        shouldValidate: true,
        shouldTouch: true,
      },
    );
  };

  const { editable } = useFormState();

  return (
    <>
      <>
        <FullContainer
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Text variant="HeadingXSmall">{t('common:passport')}</Text>
          <EndContent>
            {editable && (
              <>
                {!!passportCsvData.length && (
                  <>
                    <Separator gap={16} />
                    <Select
                      options={passportOptions}
                      value={passportValue}
                      onChange={(params) => {
                        setPassportValue(params.value);
                        if (params.value.length) {
                          onSetPassport(params.value[0].extra);
                        }
                      }}
                      clearable={false}
                      maxWidth={293}
                    />
                  </>
                )}
                <Separator gap={16} />
                <ParsePassport
                  text={
                    passportCsvData.length ? t('common:reupload') : undefined
                  }
                  onResult={(result: PassportType[]) => {
                    onChangePassport({
                      result,
                      isReupload: !!passportCsvData.length,
                    });
                  }}
                  type="passport"
                />
                {!value && <Separator gap={16} />}
              </>
            )}
            {!value && editable && (
              <Button
                buttonType="secondary"
                onClick={onAddPassport}
                startEnhancer={(color, size) => (
                  <PlusCircleSVG size={size} color={color} />
                )}
              >
                {t('common:add')}
              </Button>
            )}
          </EndContent>
        </FullContainer>
        <SeparatorVertical $margin={16} />
        {value ? (
          <>
            <CardSection style={{ paddingBottom: 0 }}>
              <SectionContainer>
                <OneThirdContainer>
                  <EnumSelectInput
                    name="passport.type"
                    enumType={EnumList.PassportType}
                    label={t('common:type')}
                    placeholder={t('common:type')}
                  />
                </OneThirdContainer>
                <OneThirdContainer>
                  <Input
                    type="text"
                    name="passport.number"
                    label={t('common:document_number')}
                    placeholder={t('common:document_number')}
                  />
                </OneThirdContainer>
                <OneThirdContainer>
                  <Input
                    type="text"
                    name="passport.identifierNumber"
                    label={t('common:ktp')}
                    placeholder={t('common:ktp')}
                  />
                </OneThirdContainer>
              </SectionContainer>
              <SectionContainer>
                <OneThirdContainer>
                  <Input
                    type="text"
                    name="passport.familyName"
                    label={t('common:family_name')}
                    placeholder={t('common:family_name')}
                  />
                </OneThirdContainer>
                <OneThirdContainer>
                  <Input
                    type="text"
                    name="passport.givenName"
                    label={t('common:given_name')}
                    placeholder={t('common:given_name')}
                  />
                </OneThirdContainer>
                <OneThirdContainer>
                  <Input
                    type="text"
                    name="passport.nationality"
                    label={t('common:nationality')}
                    placeholder={t('common:nationality')}
                  />
                </OneThirdContainer>
              </SectionContainer>
              <SectionContainer>
                <OneThirdContainer>
                  <Input
                    type="radio-group"
                    name="passport.gender"
                    options={[
                      { value: 'male', children: t('common:male') },
                      { value: 'female', children: t('common:female') },
                    ]}
                    label={t('common:gender')}
                    align="horizontal"
                  />
                </OneThirdContainer>
                <OneThirdContainer>
                  <Input
                    type="text"
                    name="passport.placeOfBirth"
                    label={t('common:place_of_birth')}
                    placeholder={t('common:place_of_birth')}
                  />
                </OneThirdContainer>
                <OneThirdContainer>
                  <Input
                    type="date-time"
                    name="passport.dateOfBirth"
                    label={t('common:date_of_birth')}
                    placeholder={t('common:date_of_birth')}
                  />
                </OneThirdContainer>
              </SectionContainer>
              <SectionContainer>
                <OneThirdContainer>
                  <Input
                    type="text"
                    name="passport.issueState"
                    label={t('common:issue_state')}
                    placeholder={t('common:issue_state')}
                  />
                </OneThirdContainer>
                <OneThirdContainer>
                  <Input
                    type="date-time"
                    name="passport.expiredAt"
                    label={t('common:date_of_expiry')}
                    placeholder={t('common:date_of_expiry')}
                  />
                </OneThirdContainer>
                <OneThirdContainer>
                  <Input
                    type="files"
                    name="passport.files"
                    label={t('common:upload_document')}
                    valueKey="file"
                    previewKey="url"
                    fileType={UploadFileTypeEnum.GuestPassport}
                    required
                  />
                </OneThirdContainer>
              </SectionContainer>
              {editable && (
                <>
                  <Button
                    buttonType="secondary"
                    startEnhancer={(color, size) => (
                      <TrashSVG
                        width={size}
                        height={size}
                        color={stylesColors.redDefault}
                      />
                    )}
                    isError
                    onClick={onRemovePassport}
                  >
                    {t('common:remove')}
                  </Button>
                  <SeparatorVertical $margin={14}>
                    <div style={{ width: 2, height: 2 }} />
                  </SeparatorVertical>
                </>
              )}
            </CardSection>
            <SeparatorVertical $margin={14}>
              <div style={{ width: 2, height: 2 }} />
            </SeparatorVertical>
          </>
        ) : (
          <Text variant="ParagraphSmall">
            {t('common:you_have_no_document_at_this_moment', {
              extra: t('common:passport').toLowerCase(),
            })}
          </Text>
        )}
      </>
    </>
  );
}

const SectionContainer = themedStyled(RawSectionContainer, {
  width: '100%',
});

const EndContent = themedStyled('div', ({ $theme }) => ({
  display: 'flex',
  width: '100%',
  justifyContent: 'center',
  alignItems: 'end',
  flexDirection: 'column',
  [`${$theme.mediaQuery.sm}`]: {
    flex: 1,
    justifyContent: 'end',
    alignItems: 'center',
    flexDirection: 'row',
  },
}));
